import { ETL_INTEGRATION_TYPE, Values } from '../components/configuration/types';
import {
  AdherenceTaskType,
  ConfigurationType,
  HistoricalDataImportTaskType,
  MediaImportStreamType,
  RealTimeDataStreamType,
  STREAM_NAMES,
  TASK_NAMES,
  USER_SYNC_FILTER_TEAMS_OPTIONS,
  UserSynchronizationTaskType,
  WebexCCConfigurationType,
  WebexCallingConfigurationType,
  ZoomCCConfigurationType,
} from '../scene/integrations/types';

export const getWebexCCRootState = (configuration: WebexCCConfigurationType) => {
  const {
    configurationVariables,
    startTime,
    configurationProperties,
    configurationName,
    integration,
    id,
    state,
    conflictedRoles,
  } = configuration;

  return {
    state,
    conflictedRoles,
    integrationId: id,
    ...(configurationProperties ?? {}),
    ...(configurationVariables ?? {}),
    configurationName,
    integration,
    startTime,
  };
};

export const getWebexCallingRootState = (configuration: WebexCallingConfigurationType) => {
  const {
    configurationVariables,
    startTime,
    configurationProperties,
    configurationName,
    integration,
    id,
    state,
    conflictedRoles,
  } = configuration;

  return {
    state,
    conflictedRoles,
    integrationId: id,
    ...(configurationProperties ?? {}),
    ...(configurationVariables ?? {}),
    configurationName,
    integration,
    startTime,
  };
};

export const getZoomCCRootState = (configuration: ZoomCCConfigurationType) => {
  const { configurationProperties, configurationVariables, configurationName, integration, id, state } = configuration;

  return {
    state,
    integrationId: id,
    ...(configurationProperties ?? {}),
    ...(configurationVariables ?? {}),
    configurationName,
    integration,
  };
};

export const getRootState = (configuration: ConfigurationType) => ({
  integrationId: configuration.id,
  configurationName: configuration.configurationName,
  state: configuration.state,
});

export const getStreamState = (state: ConfigurationType, name: string) => {
  const streams = state.streams ?? [];

  const { enabled, streamName, streamProperties, streamVariables } =
    streams.find((stream) => stream.streamName === name) ?? {};

  return {
    enabled,
    streamName,
    streamProperties,
    streamVariables,
  };
};

export const getTaskState = (state: ConfigurationType, name: string) => {
  const tasks = state.tasks ?? [];

  const { cronExpression, scheduled, taskName, taskProperties, taskVariables } =
    tasks.find((task) => task.taskName === name) ?? {};

  return {
    cronExpression,
    scheduled,
    taskName,
    taskProperties,
    taskVariables,
  };
};
export const getAdherenceState = (state: ConfigurationType, integrationType: Values<typeof ETL_INTEGRATION_TYPE>) =>
  getTaskState(state, TASK_NAMES.adherence(integrationType)) as AdherenceTaskType;

export const getHistoricalDataState = (
  state: ConfigurationType,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>
) => getTaskState(state, TASK_NAMES.historicalData(integrationType)) as HistoricalDataImportTaskType;

export const getUserSynchronizationState = (
  state: ConfigurationType,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>
) => getTaskState(state, TASK_NAMES.userSynchronization(integrationType)) as UserSynchronizationTaskType;

export const getRealTimeDataState = (state: ConfigurationType, integrationType: Values<typeof ETL_INTEGRATION_TYPE>) =>
  getStreamState(state, TASK_NAMES.realTimeData(integrationType)) as RealTimeDataStreamType;

export const getMediaImportState = (state: ConfigurationType, integrationType: Values<typeof ETL_INTEGRATION_TYPE>) =>
  getStreamState(state, TASK_NAMES.mediaImport(integrationType)) as MediaImportStreamType;

export const parseRoleMapping = (roleMapping?: string) => {
  if (!roleMapping) {
    return {};
  }

  // failsafe
  try {
    return JSON.parse(roleMapping);
  } catch (_) {
    return {};
  }
};

export const parseGroupSupervisors = (groupSupervisors?: string): string[] => {
  if (!groupSupervisors) {
    return [];
  }

  // failsafe
  try {
    return JSON.parse(groupSupervisors);
  } catch (_) {
    return [];
  }
};

/**
 * When include or exclude groups not specified in the response, we import all teams
 */
// failsafe
export const shouldImportAllTeams = (taskProperties?: { [key: string]: any }) => {
  return !(taskProperties?.includeGroups || taskProperties?.excludeGroups);
};

// failsafe
export const getTeamSelectorMode = (taskProperties?: { [key: string]: any }) => {
  if (taskProperties?.excludeGroups) {
    return USER_SYNC_FILTER_TEAMS_OPTIONS.excludeSelected;
  }

  return USER_SYNC_FILTER_TEAMS_OPTIONS.includeSelected;
};

// should never receive both includeGroups and excludeGroups filled
export const getSelectedTeams = (taskProperties?: { [key: string]: any }) => {
  if (taskProperties?.includeGroups) {
    return JSON.parse(taskProperties?.includeGroups);
  }

  if (taskProperties?.excludeGroups) {
    return JSON.parse(taskProperties?.excludeGroups);
  }

  return [];
};

/**
 * Gets the stream form order in which the form should store data
 */
export const getStreamFormOrder = (
  selectedStream: Values<typeof STREAM_NAMES>,
  availableStreams: Values<typeof STREAM_NAMES>[]
): number => {
  return availableStreams.findIndex((stream) => selectedStream.endsWith(stream));
};
